import { useState } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useRouter } from 'solito/router'

import useExecuteGoogleRecaptcha from 'app/hooks/useExecuteGoogleRecaptcha'
import useFormErrorHandler from 'app/hooks/useFormErrorHandler'
import { useSendGoogleAnalyticsEvent } from 'app/hooks/useSendGoogleAnalyticsEvent'
import { isErrorHandlerType } from 'app/services/ErrorHandler'
import {
  useUserEmailRegisterMutation,
  useUserPhoneRegisterMutation
} from 'app/services/Query/user/user.mutation'
import { USER_ROLE } from 'app/utils/constants/role.constants'
import { generateAgentToken } from 'app/utils/helpers/functions.helpers'
import {
  SignUpEmailSchema,
  SignUpPhoneSchema
} from 'app/validation/schemas/user.schemas'

export const useSignUpPersonalForm = () => {
  const { push } = useRouter()
  const { sendGA4Event } = useSendGoogleAnalyticsEvent()

  const [isPhoneForm, setIsPhoneForm] = useState(false)
  const [isReadTerm, setIsReadTerm] = useState(false)

  const { mutateAsync: phoneAsyncMutate } = useUserPhoneRegisterMutation()
  const { mutateAsync: emailAsyncMutate } = useUserEmailRegisterMutation()

  const formSchema = isPhoneForm ? SignUpPhoneSchema : SignUpEmailSchema

  const methods = useForm<SignUpPersonalFV>({
    resolver: yupResolver(formSchema),
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: ''
    },
    mode: 'all'
  })

  const { isSubmitting } = methods.formState
  const handleFormError = useFormErrorHandler(methods.setError)
  const executeGoogleRecaptcha = useExecuteGoogleRecaptcha()

  const onSubmit = async (values) => {
    try {
      if (!executeGoogleRecaptcha) throw new Error()

      const captchaToken = await executeGoogleRecaptcha('onSubmit')
      const agentToken = generateAgentToken()

      const valuesDto = {
        ...values,
        ...(values.phone_number && {
          phone_number: values.phone_number.replace(' ', '')
        }),
        role: USER_ROLE.PERSONAL,
        conditions_accepted: true,
        agent_token: agentToken,
        ...(captchaToken && { captcha_token: captchaToken })
      }

      if (isPhoneForm) {
        await phoneAsyncMutate(valuesDto)
      } else {
        await emailAsyncMutate(valuesDto)
      }

      const queryType = isPhoneForm ? 'phone' : 'email'
      const queryContact = isPhoneForm
        ? `number=${encodeURIComponent(valuesDto.phone_number)}`
        : `email=${encodeURIComponent(valuesDto.email)}`

      sendGA4Event({ eventName: 'sign_up', method: queryType })

      push(`/sign-up/verify?type=${queryType}&${queryContact}`)
    } catch (error) {
      if (isErrorHandlerType(error)) handleFormError(error?.data)
    }
  }

  const handleChangeForm = () => {
    setIsPhoneForm((prev) => !prev)
    methods.reset({
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      password_confirmation: ''
    })
  }

  return {
    methods,
    isPhoneForm,
    isReadTerm,
    isSubmitting,
    onSubmit,
    handleChangeForm,
    setIsReadTerm
  }
}
